import React, { useMemo } from 'react';
import { variantClass } from '../../../../../util/variant';
import '../documentsVersion2.css';

const resolveSaveState = ({ autoSaveStatus, pendingChangesCount }) => {
    if (autoSaveStatus === 'error') return { tone: 'error', label: 'Not saved' };
    if (autoSaveStatus === 'saving' || autoSaveStatus === 'resyncing') return { tone: 'busy', label: 'Saving…' };
    if (Number(pendingChangesCount) > 0) return { tone: 'busy', label: 'Saving…' };
    return { tone: 'ok', label: 'All changes saved' };
};

const DocumentFirstChrome = ({
    clientName,
    subtitle,
    steps = [],
    activeStep,
    autoSaveStatus,
    pendingChangesCount,
    onSaveAndExit,
    variant,
    children,
}) => {
    const stepCount = steps.length;
    const activeIndex = Number(activeStep) > 0 ? Number(activeStep) : 0;

    const completedFraction = stepCount > 0
        ? Math.min(Math.max(activeIndex - 1, 0), stepCount) / stepCount
        : 0;
    const completedPercent = Math.round(completedFraction * 100);

    const saveState = useMemo(
        () => resolveSaveState({ autoSaveStatus, pendingChangesCount }),
        [autoSaveStatus, pendingChangesCount],
    );

    return (
        <div
            className={`docfirst docfirst__page ${variantClass(variant)}`.trim()}
            data-testid='business-v2-document-first-chrome'
        >
            <div className='docfirst__appbar'>
                <div className='docfirst__appbar-inner'>
                    <div className='docfirst__appbar-identity'>
                        <span className='docfirst__appbar-client' data-testid='business-v2-document-first-client'>
                            {clientName}
                        </span>
                        {subtitle ? (
                            <span className='docfirst__appbar-meta'>{subtitle}</span>
                        ) : null}
                    </div>
                    <div className='docfirst__appbar-actions'>
                        <span
                            className={`docfirst__savestate docfirst__savestate--${saveState.tone}`}
                            data-testid='business-v2-document-first-savestate'
                        >
                            <span className='docfirst__savestate-dot' aria-hidden='true' />
                            {saveState.label}
                        </span>
                        {onSaveAndExit ? (
                            <button
                                type='button'
                                className='docfirst__appbar-exit'
                                onClick={onSaveAndExit}
                                data-testid='business-v2-document-first-exit'
                            >
                                Save and exit
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>

            {stepCount > 1 ? (
                <>
                    <div className='docfirst__stepbar'>
                        <div className='docfirst__stepbar-inner'>
                            <div className='docfirst__chips' data-testid='business-v2-document-first-stepper'>
                                {steps.map((stepItem, index) => {
                                    const isActive = index + 1 === activeIndex;
                                    const isDone = index + 1 < activeIndex;
                                    return (
                                        <span
                                            key={stepItem?.key || index}
                                            title={stepItem?.label}
                                            aria-current={isActive ? 'step' : undefined}
                                            data-testid={`business-v2-document-first-chip-${stepItem?.key || index}`}
                                            className={[
                                                'docfirst__chip',
                                                isActive ? 'docfirst__chip--active' : '',
                                                isDone ? 'docfirst__chip--done' : '',
                                            ].filter(Boolean).join(' ')}
                                        >
                                            <span className='docfirst__chip-index'>{index + 1}</span>
                                            <span className='docfirst__chip-label'>{stepItem?.label}</span>
                                        </span>
                                    );
                                })}
                            </div>
                            <span
                                className='docfirst__stepbar-percent'
                                data-testid='business-v2-document-first-percent'
                            >
                                {`${completedPercent}% complete`}
                            </span>
                        </div>
                    </div>

                    <div className='docfirst__hairline' aria-hidden='true'>
                        <span className='docfirst__hairline-fill' style={{ width: `${completedPercent}%` }} />
                    </div>
                </>
            ) : null}

            <div className='docfirst__page-body'>{children}</div>
        </div>
    );
};

export default DocumentFirstChrome;

